import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import { AppState, type AppStateStatus } from 'react-native';

import { dealsStore } from '@/stores/deals.store';

/**
 * Deal Steps v2: while the deal screen is focused, the app is in the
 * foreground, and the user's action can still change, poll the action every
 * 20 s so a provider confirmation turns a step green without a pull. Focus
 * (including a step_confirmed push tap) refreshes once right away, so the
 * screen never shows 20 s of stale state. Going to the brand and coming back
 * refreshes again and resumes polling; the background suspends it. Stops on
 * blur, and the store stops itself once the action settles. A deal with no
 * action yet still polls: a postback can create the action.
 */
export function useDealActionPolling(dealId: number | undefined, settled: boolean) {
  useFocusEffect(
    useCallback(() => {
      if (dealId === undefined || settled) return undefined;
      const refreshNow = () => {
        void Promise.resolve()
          // Opening the page (or coming back to it) is a user-visible moment:
          // ask for a provider sync. The 20s interval below never does.
          .then(() => dealsStore.refreshAction(dealId, { sync: true }))
          .catch(() => {});
      };
      refreshNow();
      // Anything but a known background state counts as foreground ("unknown"
      // on a cold start, before the first AppState event).
      const backgrounded = (state: AppStateStatus) => state === 'background' || state === 'inactive';
      if (!backgrounded(AppState.currentState)) dealsStore.startActionPolling(dealId);
      const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
        if (backgrounded(state)) {
          dealsStore.stopActionPolling();
        } else {
          refreshNow();
          dealsStore.startActionPolling(dealId);
        }
      });
      return () => {
        sub.remove();
        dealsStore.stopActionPolling();
      };
    }, [dealId, settled]),
  );
}
